import { Check } from 'lucide-react'

export interface WizardStep {
  number: number
  title: string
}

interface Props<TStep extends WizardStep> {
  steps: TStep[]
  currentStep: number
  completedSteps: Set<number>
  onStepClick: (step: number) => void
  variant?: 'mobile' | 'desktop' | 'both' | 'tabs'
}

/**
 * Flow-agnostic wizard step navigation.
 *
 * - 'tabs': full-width horizontal step header, always visible (primary layout).
 * - 'mobile': horizontal scrollable step pills, hidden at xl+.
 * - 'desktop': vertical sidebar, shown at xl+.
 * - 'both': mobile + desktop.
 *
 * A step is clickable when it is the current step or has been completed.
 * Shared by the CT600 and Self-Assessment wizards.
 */
export default function WizardStepNav<TStep extends WizardStep>({
  steps,
  currentStep,
  completedSteps,
  onStepClick,
  variant = 'both',
}: Props<TStep>) {
  const showTabs = variant === 'tabs'
  const showMobile = variant === 'mobile' || variant === 'both'
  const showDesktop = variant === 'desktop' || variant === 'both'

  return (
    <>
      {/* Horizontal step pills — always visible for 'tabs', xl:hidden for 'mobile'/'both' */}
      {(showTabs || showMobile) && (
        <div className={`${showTabs ? '' : 'xl:hidden '}w-full overflow-x-auto scrollbar-hide`}>
          <div className='flex gap-2 pb-2 min-w-max'>
            {steps.map(step => {
              const isActive = step.number === currentStep
              const isCompleted = completedSteps.has(step.number)
              const isClickable = isCompleted || step.number === currentStep

              return (
                <button
                  key={step.number}
                  onClick={() => isClickable && onStepClick(step.number)}
                  disabled={!isClickable}
                  className={`flex items-center gap-2 rounded-xl px-3 py-2 text-left transition-all whitespace-nowrap ${
                    isActive
                      ? 'bg-gray-900 text-white shadow-lg'
                      : isCompleted
                        ? 'bg-white text-gray-900 hover:bg-gray-50 border border-gray-200'
                        : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  <div
                    className={`flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full text-xs font-bold ${
                      isActive
                        ? 'bg-white text-gray-900'
                        : isCompleted
                          ? 'bg-green-100 text-green-700'
                          : 'bg-gray-200 text-gray-500'
                    }`}
                  >
                    {isCompleted ? <Check className='h-3 w-3' /> : step.number}
                  </div>
                  <span className='text-xs font-medium'>{step.title}</span>
                </button>
              )
            })}
          </div>
        </div>
      )}

      {/* Desktop: vertical sidebar */}
      {showDesktop && (
        <div className='hidden xl:block w-64 flex-shrink-0'>
          <div className='sticky top-8 space-y-2'>
            {steps.map(step => {
              const isActive = step.number === currentStep
              const isCompleted = completedSteps.has(step.number)
              const isClickable = isCompleted || step.number === currentStep

              return (
                <button
                  key={step.number}
                  onClick={() => isClickable && onStepClick(step.number)}
                  disabled={!isClickable}
                  className={`flex w-full items-center gap-3 rounded-xl px-4 py-3 text-left transition-all ${
                    isActive
                      ? 'bg-gray-900 text-white shadow-lg'
                      : isCompleted
                        ? 'bg-white text-gray-900 hover:bg-gray-50 border border-gray-200'
                        : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  <div
                    className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-sm font-bold ${
                      isActive
                        ? 'bg-white text-gray-900'
                        : isCompleted
                          ? 'bg-green-100 text-green-700'
                          : 'bg-gray-200 text-gray-500'
                    }`}
                  >
                    {isCompleted ? <Check className='h-4 w-4' /> : step.number}
                  </div>
                  <span className='text-sm font-medium'>{step.title}</span>
                </button>
              )
            })}
          </div>
        </div>
      )}
    </>
  )
}
